import { inject, injectable } from 'inversify'
import type { INativeNotification } from '@/services/types'
import type LoggerService from '@/services/loggerService'
import { SERVICE_TYPES } from '@/core/container/types'
import LogoImageUrl from '@/assets/images/logo.png'

@injectable()
export default class NativeNotificationService implements INativeNotification {
  constructor(@inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService) {}

  public isHasNotificationFeature(): boolean {
    return Boolean('Notification' in window)
  }

  public isNotificationDenied() {
    return this.isHasNotificationFeature() && Notification.permission === 'denied'
  }

  public isNotificationGranted() {
    return this.isHasNotificationFeature() && Notification.permission === 'granted'
  }

  public isAllowNotificationRequest() {
    return this.isHasNotificationFeature() && Notification.permission === 'default'
  }

  public notificationRequest(callback?: (result: string) => void) {
    if (this.isAllowNotificationRequest()) {
      Notification.requestPermission(callback)
    }
  }

  public notify(tag: string, title: string, text: string, icon?: string, onClickCallback?: () => void) {
    if (!this.isNotificationGranted()) {
      return
    }
    icon = icon || LogoImageUrl
    let notification: Notification
    try {
      notification = new Notification(title, {
        icon,
        tag,
        body: text,
      })
    } catch (e) {
      // Nearly all mobile browsers throw here and want a service worker, which the app does not register
      // https://developer.mozilla.org/en-US/docs/Web/API/Notification/Notification
      this.loggerService.error('error', `Native notification is not available: ${(e as Error).message}`)
      return
    }
    if (onClickCallback) {
      notification.onclick = () => {
        try {
          onClickCallback()
        } catch (e) {
          this.loggerService.error('error', (e as Error).message)
        }
        window.focus()
        notification.close()
      }
    }
  }
}
